// ===================== Input (keyboard + mouse + touch joystick + gamepad) =====================
import { norm } from './math.js';

// Gamepad buttons that raise synthetic key edges (standard mapping).
const GP_BUTTONS = { 0: 'gp_a', 1: 'gp_b', 9: 'gp_start', 12: 'gp_up', 13: 'gp_down', 14: 'gp_left', 15: 'gp_right' };

export class Input {
  constructor(canvas) {
    this.canvas = canvas;
    this.keys = new Set();
    this.mouse = { x: 0, y: 0, down: false };   // screen-space
    this.justPressed = new Set();
    this._edge = new Set();   // keydown edges captured since last update()

    // touch joystick state
    this.touch = { active: false, id: null, ox: 0, oy: 0, dx: 0, dy: 0 };
    this.isTouch = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

    // gamepad state (polled each frame — the Gamepad API has no events for buttons)
    this.gamepad = { connected: false, moveX: 0, moveY: 0 };
    this._gpPrev = {};
    this._gpAxPrev = [0, 0];
    // Connection-transition hooks, assigned by main.js (may stay null).
    // Browsers only surface a pad in getGamepads() after its first input, so
    // onGamepadConnect effectively fires on the pad's first button press.
    this.onGamepadConnect = null;
    this.onGamepadDisconnect = null;

    this._bind();
  }

  _bind() {
    window.addEventListener('keydown', (e) => {
      // Prevent page scroll on arrows / space.
      if ([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
      const k = e.key.toLowerCase();
      // Capture the press edge at event time so fast taps are never missed,
      // even if the key is released before the next frame's update().
      if (!this.keys.has(k)) this._edge.add(k);
      this.keys.add(k);
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.key.toLowerCase()));
    window.addEventListener('blur', () => this.keys.clear());

    const rectPos = (clientX, clientY) => {
      const r = this.canvas.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };

    this.canvas.addEventListener('mousemove', (e) => {
      const p = rectPos(e.clientX, e.clientY);
      this.mouse.x = p.x; this.mouse.y = p.y;
    });
    this.canvas.addEventListener('mousedown', () => { this.mouse.down = true; });
    window.addEventListener('mouseup', () => { this.mouse.down = false; });

    // Touch: left half = movement joystick, spawns where finger lands.
    const stick = document.getElementById('touch-stick');
    const knob = document.getElementById('touch-knob');
    const showStick = (x, y) => {
      if (!stick) return;
      stick.classList.remove('hidden');
      stick.style.left = (x - 60) + 'px';
      stick.style.top = (y - 60) + 'px';
      if (knob) { knob.style.left = '50%'; knob.style.top = '50%'; }
    };
    const moveKnob = (dx, dy) => {
      if (!knob) return;
      knob.style.left = (50 + dx * 40) + '%';
      knob.style.top = (50 + dy * 40) + '%';
    };
    const hideStick = () => { if (stick) stick.classList.add('hidden'); };

    this.canvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (!this.touch.active) {
          const p = rectPos(t.clientX, t.clientY);
          this.touch.active = true; this.touch.id = t.identifier;
          this.touch.ox = p.x; this.touch.oy = p.y; this.touch.dx = 0; this.touch.dy = 0;
          showStick(p.x, p.y);
        }
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier === this.touch.id) {
          const p = rectPos(t.clientX, t.clientY);
          let dx = p.x - this.touch.ox, dy = p.y - this.touch.oy;
          const max = 55;
          const l = Math.hypot(dx, dy) || 1;
          if (l > max) { dx = dx / l * max; dy = dy / l * max; }
          this.touch.dx = dx / max; this.touch.dy = dy / max;
          moveKnob(this.touch.dx, this.touch.dy);
        }
      }
    }, { passive: false });

    const endTouch = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this.touch.id) {
          this.touch.active = false; this.touch.id = null;
          this.touch.dx = 0; this.touch.dy = 0;
          hideStick();
        }
      }
    };
    this.canvas.addEventListener('touchend', endTouch);
    this.canvas.addEventListener('touchcancel', endTouch);

    window.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // Normalized movement vector [x,y] from WASD/arrows, touch joystick or gamepad.
  moveVector() {
    let x = 0, y = 0;
    const k = this.keys;
    if (k.has('a') || k.has('arrowleft')) x -= 1;
    if (k.has('d') || k.has('arrowright')) x += 1;
    if (k.has('w') || k.has('arrowup')) y -= 1;
    if (k.has('s') || k.has('arrowdown')) y += 1;
    if (x || y) return norm(x, y);
    if (this.touch.active && (this.touch.dx || this.touch.dy)) {
      const l = Math.hypot(this.touch.dx, this.touch.dy);
      if (l > 0.12) return [this.touch.dx / Math.max(l, 1), this.touch.dy / Math.max(l, 1)];
    }
    const gx = this.gamepad.moveX, gy = this.gamepad.moveY;
    if (gx || gy) {
      const l = Math.hypot(gx, gy);
      return l > 1 ? [gx / l, gy / l] : [gx, gy];  // preserve analog magnitude
    }
    return [0, 0];
  }

  keyDown(key) { return this.keys.has(key); }

  // Poll the first connected gamepad: left stick + d-pad movement, button edges.
  _pollGamepad() {
    if (this._gpBlocked) return;
    let pads;
    try {
      pads = navigator.getGamepads ? navigator.getGamepads() : null;
    } catch {
      // Sandboxed iframes can block the Gamepad API entirely (SecurityError).
      this._gpBlocked = true;
      return;
    }
    if (!pads) return;
    let gp = null;
    for (const p of pads) if (p && p.connected) { gp = p; break; }
    const was = this.gamepad.connected;
    this.gamepad.connected = !!gp;
    if (!gp) {
      this.gamepad.moveX = 0; this.gamepad.moveY = 0; this._gpPrev = {};
      if (was && this.onGamepadDisconnect) this.onGamepadDisconnect();
      return;
    }
    if (!was && this.onGamepadConnect) this.onGamepadConnect();

    const btn = (i) => !!(gp.buttons[i] && gp.buttons[i].pressed);
    let ax = gp.axes[0] || 0, ay = gp.axes[1] || 0;
    if (Math.hypot(ax, ay) < 0.18) { ax = 0; ay = 0; }     // deadzone
    if (btn(14)) ax = -1; else if (btn(15)) ax = 1;
    if (btn(12)) ay = -1; else if (btn(13)) ay = 1;
    this.gamepad.moveX = ax; this.gamepad.moveY = ay;

    for (const i in GP_BUTTONS) {
      const down = btn(+i);
      if (down && !this._gpPrev[i]) this._edge.add(GP_BUTTONS[i]);
      this._gpPrev[i] = down;
    }
    // Stick flicks double as d-pad edges for menu navigation.
    const [px, py] = this._gpAxPrev;
    if (ax < -0.5 && px >= -0.5) this._edge.add('gp_left');
    if (ax > 0.5 && px <= 0.5) this._edge.add('gp_right');
    if (ay < -0.5 && py >= -0.5) this._edge.add('gp_up');
    if (ay > 0.5 && py <= 0.5) this._edge.add('gp_down');
    this._gpAxPrev[0] = ax; this._gpAxPrev[1] = ay;   // mutate in place — no per-frame allocation
  }

  // Called once per frame; publishes the keydown edges captured since last call.
  update() {
    this._pollGamepad();
    this.justPressed = this._edge;
    this._edge = new Set();
  }
  pressed(key) { return this.justPressed.has(key); }
}
